import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { House, Disc3, ShieldHalf, LogIn, LogOut, Crown, Search, Menu, X, Skull, Mic2, ListMusic, Trophy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import PlayerBar from "@/components/PlayerBar";
import PremiumOverlay from "@/components/PremiumOverlay";
import InstallButton from "@/components/InstallButton";
import InstallPrompt from "@/components/InstallPrompt";

const NavItem = ({ to, icon: Icon, label, active, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    data-testid={`nav-${label.toLowerCase()}`}
    className={`flex items-center gap-3 px-4 py-3 border-l-4 transition-colors ${
      active ? "border-primary bg-primary/10 text-white" : "border-transparent text-muted-foreground hover:text-white hover:bg-white/5"
    }`}
  >
    <Icon size={20} weight="bold" />
    <span className="overline text-xs font-semibold">{label}</span>
  </Link>
);

export default function Layout({ children }) {
  const { user, isGuest, isPremium, isAdmin, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const links = (onClick) => (
    <>
      <NavItem to="/" icon={House} label="Home" active={loc.pathname === "/"} onClick={onClick} />
      <NavItem to="/browse" icon={Disc3} label="Browse" active={loc.pathname === "/browse"} onClick={onClick} />
      <NavItem to="/playlists" icon={ListMusic} label="Playlists" active={loc.pathname === "/playlists"} onClick={onClick} />
      <NavItem to="/compilation" icon={Trophy} label="Compilation" active={loc.pathname === "/compilation"} onClick={onClick} />
      <NavItem to="/artist" icon={Mic2} label="Artist" active={loc.pathname === "/artist" || loc.pathname === "/contact"} onClick={onClick} />
      {isAdmin && (
        <NavItem to="/admin" icon={ShieldHalf} label="Admin" active={loc.pathname.startsWith("/admin")} onClick={onClick} />
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-border bg-card fixed h-screen z-40">
        <Link to="/" data-testid="logo-desktop" className="flex items-center gap-2 px-6 py-6 border-b border-border">
          <Skull className="text-primary" size={28} />
          <span className="font-display text-xl font-bold tracking-tight leading-none">
            FUCK<br />THE OPS
          </span>
        </Link>
        <nav className="flex flex-col py-4 flex-1">{links()}</nav>
        <div className="px-4 pb-2">
          <InstallButton variant="sidebar" />
        </div>
        <div className="p-4 border-t border-border space-y-3">
          {!isPremium && !isGuest && (
            <Button data-testid="sidebar-go-premium" onClick={() => nav("/subscribe")} className="w-full bg-primary hover:bg-white hover:text-primary rounded-none font-bold text-xs overline">
              <Crown size={16} className="mr-2" /> Go Premium
            </Button>
          )}
          {isGuest ? (
            <Button data-testid="sidebar-login" onClick={() => nav("/login")} variant="outline" className="w-full rounded-none border-2 text-xs overline font-bold">
              <LogIn size={16} className="mr-2" /> Log In
            </Button>
          ) : (
            <div className="space-y-2">
              <div className="text-xs text-muted-foreground truncate" data-testid="sidebar-user">
                {user?.name} {isPremium && <span className="text-accent">· PREMIUM</span>}
              </div>
              <Button data-testid="sidebar-logout" onClick={logout} variant="outline" className="w-full rounded-none border-2 text-xs overline font-bold">
                <LogOut size={16} className="mr-2" /> Logout
              </Button>
            </div>
          )}
          <Link to="/admin" data-testid="sidebar-admin-link" className="flex items-center justify-center gap-2 pt-1 text-[11px] overline text-muted-foreground hover:text-primary transition-colors">
            <ShieldHalf size={13} /> Admin
          </Link>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="md:hidden fixed top-0 inset-x-0 h-16 bg-card border-b border-border z-40 flex items-center justify-between px-4">
        <Link to="/" data-testid="logo-mobile" className="flex items-center gap-2">
          <Skull className="text-primary" size={24} />
          <span className="font-display font-bold text-lg tracking-tight">FTO</span>
        </Link>
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="text-white p-2">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {open && (
        <div className="md:hidden fixed inset-0 top-16 bg-background z-40 flex flex-col">
          <nav className="flex flex-col py-4">{links(() => setOpen(false))}</nav>
          <div className="px-4">
            <InstallButton variant="mobile" onNavigate={() => setOpen(false)} />
          </div>
          <div className="p-4 border-t border-border space-y-3 mt-auto mb-24">
            {!isPremium && !isGuest && (
              <Button data-testid="mobile-go-premium" onClick={() => { setOpen(false); nav("/subscribe"); }} className="w-full bg-primary rounded-none font-bold text-xs overline">
                <Crown size={16} className="mr-2" /> Go Premium
              </Button>
            )}
            {isGuest ? (
              <Button data-testid="mobile-login" onClick={() => { setOpen(false); nav("/login"); }} variant="outline" className="w-full rounded-none border-2 text-xs overline font-bold">Log In</Button>
            ) : (
              <Button data-testid="mobile-logout" onClick={() => { setOpen(false); logout(); }} variant="outline" className="w-full rounded-none border-2 text-xs overline font-bold">Logout ({user?.name})</Button>
            )}
            <Link to="/admin" data-testid="mobile-admin-link" onClick={() => setOpen(false)} className="flex items-center justify-center gap-2 pt-1 text-[11px] overline text-muted-foreground hover:text-primary transition-colors">
              <ShieldHalf size={13} /> Admin
            </Link>
          </div>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 md:ml-64 pt-16 md:pt-0 pb-40 min-h-screen">{children}</main>

      <PlayerBar />
      <PremiumOverlay />
      <InstallPrompt />
    </div>
  );
}
